import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useLoading } from '@/composables/useLoading'

/**
 * Composable for side-effect API operations (create, update, delete).
 *
 * @param {Object} options
 * @param {boolean} options.useOverlay - Show full-screen side-effect overlay (default: false)
 *
 * @example basic mutation
 * const { mutate, loading } = useMutation()
 * await mutate(() => createAdmin(form), {
 *   success: 'Successfully created!',
 *   onSuccess: () => { dialogVisible.value = false; getList() }
 * })
 *
 * @example with overlay (e.g. delete)
 * const { mutate } = useMutation({ useOverlay: true })
 * await mutate(() => deleteAdmin(row.id), {
 *   success: 'Successfully deleted!',
 *   onSuccess: () => getList(),
 *   onError: () => { row.status = originalStatus.value }
 * })
 *
 * @example silent mutation (no success message)
 * await mutate(() => updateStatus(id, { status }), {
 *   onSuccess: () => getList(),
 *   onError: () => { row.status = previousStatus }
 * })
 */
export function useMutation(options = {}) {
  const { useOverlay = true } = options
  const { setLoading } = useLoading()

  const loading = ref(false)

  async function mutate(apiFn, callbackOptions = {}) {
    const { success, onSuccess, onError } = callbackOptions

    try {
      loading.value = true
      if (useOverlay) setLoading(true)

      await apiFn()

      if (success) ElMessage.success(success)
      await onSuccess?.()
    } catch (e) {
      console.log(e)
      // error handled by request interceptor
      await onError?.()
    } finally {
      loading.value = false
      if (useOverlay) setLoading(false)
    }
  }

  return { mutate, loading }
}
